/**
 * Trendy Home — product page.
 * Gallery, fullscreen zoom, variant picker, option modal, share, recently viewed.
 * Loaded only on the product template.
 */

const root = document.querySelector('[data-product-root]');
const strings = window.themeStrings || {};

/*
 * Formats through the shop's money_format rather than Intl, so a price re-rendered on a
 * variant change is byte-identical to the one Liquid printed. Intl('ar-EG') gave
 * "٣٩٩٫٠٠ ج.م." next to Liquid's "LE 399.00" on every product that has options.
 */
const money = (cents) => {
  const format = window.themeMoneyFormat || 'LE {{amount}}';
  const value = cents / 100;

  const group = (n, decimals, sep, dec) => {
    const fixed = n.toFixed(decimals);
    const [int, frac] = fixed.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return frac ? `${grouped}${dec}${frac}` : grouped;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    switch (token) {
      case 'amount_no_decimals':
        return group(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return group(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return group(value, 2, "'", '.');
      default:
        return group(value, 2, ',', '.');
    }
  });
};

/* ----------------------------------------------------------------- gallery */

const frames = [...(root?.querySelectorAll('.gal__img') || [])];
const thumbs = [...(root?.querySelectorAll('.gal__thumb') || [])];
let index = 0;

function showFrame(next) {
  if (!frames.length) return;

  /* Wraps at both ends, so the arrows never dead-end on the first or last image. */
  index = (next + frames.length) % frames.length;

  frames.forEach((frame, i) => frame.classList.toggle('is-on', i === index));
  thumbs.forEach((thumb, i) => thumb.classList.toggle('is-on', i === index));

  thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });

  const zoomImg = document.querySelector('[data-zoom-img]');
  if (zoomImg) zoomImg.src = frames[index].dataset.full;
}

function initGallery() {
  if (!frames.length) return;

  root.querySelector('[data-gal-prev]')?.addEventListener('click', () => showFrame(index - 1));
  root.querySelector('[data-gal-next]')?.addEventListener('click', () => showFrame(index + 1));

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => showFrame(Number(thumb.dataset.index)));
  });

  /* Drag or swipe the stage to change frame. Mouse included — not just touch. */
  const stage = root.querySelector('.gal__stage');
  let startX = null;

  stage?.addEventListener('pointerdown', (event) => {
    /* Let the arrows and the zoom button handle their own clicks. */
    if (event.target.closest('button')) return;
    startX = event.clientX;
    stage.style.cursor = 'grabbing';
  });

  const endDrag = (event) => {
    if (startX === null) return;

    const dx = event.clientX - startX;
    startX = null;
    stage.style.cursor = '';

    if (Math.abs(dx) < 40) return;

    /* Dragging left is "forward" in English and "back" in Arabic. */
    const rtl = getComputedStyle(document.documentElement).direction === 'rtl';
    const forward = rtl ? dx > 0 : dx < 0;
    showFrame(forward ? index + 1 : index - 1);
  };

  stage?.addEventListener('pointerup', endDrag);
  stage?.addEventListener('pointerleave', endDrag);
}

/* -------------------------------------------------------------------- zoom */

function initZoom() {
  const overlay = document.querySelector('[data-zoom]');
  if (!overlay || !frames.length) return;

  const img = overlay.querySelector('[data-zoom-img]');
  const canvas = overlay.querySelector('.zoom__canvas');
  let magnified = false;

  /*
   * Fitting the full-resolution file to the screen is not zooming — it looks identical to the
   * stage. Clicking scales the image up and the pointer then drives transform-origin, which
   * pans it like a magnifier.
   */
  const setMagnified = (on, event) => {
    magnified = on;

    if (!on) {
      img.style.transform = '';
      img.style.transformOrigin = '';
      overlay.dataset.magnified = 'false';
      return;
    }

    if (event) trackOrigin(event);
    img.style.transform = 'scale(2.6)';
    overlay.dataset.magnified = 'true';
  };

  const trackOrigin = (event) => {
    const rect = canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    img.style.transformOrigin = `${clamp(x)}% ${clamp(y)}%`;
  };

  const clamp = (value) => Math.min(100, Math.max(0, value));

  const open = () => {
    img.src = frames[index].dataset.full || frames[index].src;
    setMagnified(false);
    overlay.hidden = false;
    document.documentElement.style.overflow = 'hidden';
  };

  const close = () => {
    setMagnified(false);
    overlay.hidden = true;
    document.documentElement.style.overflow = '';
  };

  root.querySelector('[data-zoom-open]')?.addEventListener('click', open);
  overlay.querySelector('[data-zoom-close]')?.addEventListener('click', close);

  overlay.querySelector('[data-zoom-prev]')?.addEventListener('click', () => {
    setMagnified(false);
    showFrame(index - 1);
  });

  overlay.querySelector('[data-zoom-next]')?.addEventListener('click', () => {
    setMagnified(false);
    showFrame(index + 1);
  });

  img.addEventListener('click', (event) => {
    event.stopPropagation();
    setMagnified(!magnified, event);
  });

  canvas.addEventListener('pointermove', (event) => {
    if (magnified) trackOrigin(event);
  });

  /* A click that lands on the backdrop — not on the image or a control — closes. */
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay || event.target === canvas) close();
  });

  document.addEventListener('keydown', (event) => {
    if (overlay.hidden) return;
    if (event.key === 'Escape') close();

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      setMagnified(false);
      showFrame(event.key === 'ArrowRight' ? index + 1 : index - 1);
    }
  });
}

/* ---------------------------------------------------------- variant picker */

function initVariants() {
  if (!root) return;

  const form = root.querySelector('[data-product-form]');
  const variants = JSON.parse(root.querySelector('[data-variants]')?.textContent || '[]');
  if (!form || !variants.length) return;

  const idInput = form.querySelector('[name="id"]');
  const button = form.querySelector('[data-add-button]');
  const label = button.querySelector('[data-add-label]');
  const buyNow = form.querySelector('[data-buy-now]');
  const priceEl = root.querySelector('[data-product-price]');
  const optionInputs = [...form.querySelectorAll('[data-option-input]')];

  /*
   * A single-variant product renders no option inputs at all, so there is nothing to match on
   * and nothing to sync — Liquid already rendered the right id, price and button state. Bail
   * out before the matcher, which would otherwise compare an empty selection against the
   * variant's ["Default Title"] and conclude the product is unavailable.
   */
  if (!optionInputs.length) return;

  const apply = () => {
    const chosen = optionInputs.filter((input) => input.checked).map((input) => input.value);

    /* Mirror the chosen value into every label that echoes it. */
    chosen.forEach((value, i) => {
      root.querySelectorAll(`[data-option-selected="${i}"]`).forEach((el) => {
        el.textContent = value;
      });
    });

    const match = variants.find(
      (variant) =>
        variant.options.length === chosen.length &&
        variant.options.every((option, i) => option === chosen[i])
    );

    if (!match) {
      button.disabled = true;
      if (buyNow) buyNow.disabled = true;
      label.textContent = strings.unavailable || '';
      return;
    }

    idInput.value = match.id;

    /* Keep the URL shareable — a copied link has to land on the same variant. */
    const url = new URL(window.location.href);
    url.searchParams.set('variant', match.id);
    window.history.replaceState({}, '', url);

    const onSale = match.compare_at_price && match.compare_at_price > match.price;

    if (priceEl) {
      priceEl.innerHTML = `
        <span class="price__amount${onSale ? ' price--sale' : ''}">${money(match.price)}</span>
        ${onSale ? `<s class="price__compare">${money(match.compare_at_price)}</s>` : ''}
        ${onSale ? savingsMarkup(match) : ''}`;
    }

    /* Mirror the price into the sticky bar so the two never disagree. */
    const barPrice = document.querySelector('[data-buybar-price]');
    if (barPrice) {
      barPrice.innerHTML = `
        <span class="price__amount${onSale ? ' price--sale' : ''}">${money(match.price)}</span>
        ${onSale ? `<s class="price__compare">${money(match.compare_at_price)}</s>` : ''}`;
    }

    showStock(match.low_stock);

    /* Jump the gallery to the image this variant is pinned to, if it has one. */
    if (match.media_id) {
      const frame = frames.findIndex((f) => Number(f.dataset.mediaId) === match.media_id);
      if (frame > -1) showFrame(frame);
    }

    button.disabled = !match.available;
    if (buyNow) buyNow.disabled = !match.available;
    label.textContent = match.available ? strings.addToCart || '' : strings.soldOut || '';

    const barButton = document.querySelector('[data-buybar-add]');
    const barLabel = document.querySelector('[data-buybar-label]');
    if (barButton) barButton.disabled = !match.available;
    if (barLabel) barLabel.textContent = label.textContent;
  };

  optionInputs.forEach((input) => input.addEventListener('change', apply));
  apply();
}

/* --------------------------------------------------------------- urgency */

/* Only ever called on a variant that really is on sale. */
function savingsMarkup(variant) {
  const template = strings.saveHtml;

  /* The setting is off — Liquid rendered no pill, so the JS must not invent one. Read from the
     attribute rather than from a previous render: landing on a full-price variant and then
     switching to a discounted one would otherwise find nothing to copy. */
  const enabled = root?.querySelector('[data-product-price]')?.hasAttribute('data-show-savings');
  if (!template || !enabled) return '';

  const diff = variant.compare_at_price - variant.price;
  const percent = Math.round((diff / variant.compare_at_price) * 100);

  return `<span class="pdp__save">${template
    .replace('__AMOUNT__', money(diff))
    .replace('__PERCENT__', percent)}</span>`;
}

/*
 * `count` is Shopify's own inventory number, or null when the variant is comfortably in
 * stock — in which case the row is hidden rather than filled with a softer claim.
 */
function showStock(count) {
  const row = root?.querySelector('[data-stock-row]');
  const text = root?.querySelector('[data-stock-text]');
  if (!row || !text) return;

  if (!count || count < 1) {
    row.hidden = true;
    return;
  }

  if (count === 1) text.textContent = strings.lowStockOne || '';
  else if (count === 2) text.textContent = strings.lowStockTwo || '';
  else text.textContent = (strings.lowStockMany || '').replace('__COUNT__', count);

  row.hidden = false;
}

/*
 * The delivery window, counted in business days from *now* rather than from whenever Shopify
 * last cached the page. Friday is the one day skipped: it is the Egyptian weekend day the
 * couriers here don't run, Saturday they do.
 */
function initDeliveryEta() {
  const row = root?.querySelector('[data-eta-row]');
  const text = root?.querySelector('[data-eta-text]');
  if (!row || !text) return;

  const min = parseInt(row.dataset.etaMin, 10);
  const max = parseInt(row.dataset.etaMax, 10);
  if (!min || !max) return;

  const addBusinessDays = (days) => {
    const date = new Date();
    let left = days;
    while (left > 0) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 5) left -= 1;
    }
    return date;
  };

  /* Latin digits with Arabic weekday and month names, to match the "LE 399.00" prices. */
  const format = (date) =>
    date.toLocaleDateString('ar-EG-u-nu-latn', { weekday: 'long', day: 'numeric', month: 'long' });

  const from = addBusinessDays(min);
  const to = addBusinessDays(max);
  const sameDay = from.toDateString() === to.toDateString();

  const template = sameDay ? strings.etaSameDayHtml : strings.etaHtml;
  if (!template) return;

  text.innerHTML = template.replace('__FROM__', format(from)).replace('__TO__', format(to));
  row.hidden = false;
}

/*
 * Counts down to the deadline the merchant typed into the section, resolved to an epoch by
 * Liquid in the shop's timezone. When it runs out the row is removed for good: no reset on
 * reload, no rolling window, nothing to walk back if a customer checks tomorrow.
 */
function initOfferCountdown() {
  const row = root?.querySelector('[data-offer-row]');
  const clock = root?.querySelector('[data-offer-clock]');
  if (!row || !clock) return;

  const endsAt = parseInt(row.dataset.offerEnds, 10);
  if (!endsAt) return;

  const pad = (n) => String(n).padStart(2, '0');

  /* Declared up front: the first tick runs before setInterval returns, and a deadline that
     has already passed takes the clearInterval path on that very first call. */
  let timer = null;

  const tick = () => {
    const left = endsAt - Math.floor(Date.now() / 1000);

    if (left <= 0) {
      row.remove();
      if (timer) clearInterval(timer);
      return;
    }

    const days = Math.floor(left / 86400);
    const hours = Math.floor((left % 86400) / 3600);
    const minutes = Math.floor((left % 3600) / 60);
    const seconds = left % 60;

    const dayPart = days > 0 ? `${(strings.offerDays || '').replace('__COUNT__', days)} ` : '';
    clock.textContent = `${dayPart}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    row.hidden = false;
  };

  tick();
  if (row.isConnected) timer = setInterval(tick, 1000);
}

/* ------------------------------------------------------- sticky buy bar */

/*
 * Shown once the real add-to-cart has scrolled out of view, and it submits that same form —
 * so quantity, variant id and the cart-drawer handler all stay in one place.
 */
function initBuyBar() {
  const bar = document.querySelector('[data-buybar]');
  const buyRow = root?.querySelector('.pdp__buy');
  const button = root?.querySelector('[data-add-button]');
  if (!bar || !buyRow || !button) return;

  bar.hidden = false;

  const observer = new IntersectionObserver(
    ([entry]) => bar.classList.toggle('is-on', !entry.isIntersecting),
    { rootMargin: '0px 0px -12px 0px' }
  );
  observer.observe(buyRow);

  bar.querySelector('[data-buybar-add]')?.addEventListener('click', () => button.click());
}

/* ----------------------------------------------------------------- buy now */

/* Wired on its own, so it still works on the single-variant products that initVariants skips. */
function initBuyNow() {
  const form = root?.querySelector('[data-product-form]');
  const buyNow = form?.querySelector('[data-buy-now]');
  if (!buyNow) return;

  buyNow.addEventListener('click', async () => {
    buyNow.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(form.querySelector('[name="id"]').value),
              quantity: Number(form.querySelector('[name="quantity"]')?.value || 1),
            },
          ],
        }),
      });

      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      window.location.href = '/checkout';
    } catch (error) {
      console.error(error);
      buyNow.disabled = false;
    }
  });
}

/* ------------------------------------------------------------ option modal */

function initOptionModal() {
  if (!root) return;

  root.querySelectorAll('[data-optmodal]').forEach((modal) => {
    const key = modal.dataset.optmodal;
    const opener = root.querySelector(`[data-optmodal-open="${key}"]`);
    const find = modal.querySelector('[data-optmodal-find]');
    const empty = modal.querySelector('[data-optmodal-empty]');
    const values = [...modal.querySelectorAll('.optmodal__val')];

    const setOpen = (open) => {
      modal.hidden = !open;
      document.documentElement.style.overflow = open ? 'hidden' : '';
      if (open) find?.focus();
    };

    opener?.addEventListener('click', () => setOpen(true));

    modal.querySelectorAll('[data-optmodal-close]').forEach((el) => {
      el.addEventListener('click', () => setOpen(false));
    });

    /* Picking a value is the end of the task — close on the label's own click. */
    values.forEach((value) => value.addEventListener('click', () => setTimeout(() => setOpen(false), 60)));

    find?.addEventListener('input', () => {
      const term = find.value.trim().toLowerCase();
      let hits = 0;

      values.forEach((value) => {
        const match = value.dataset.val.includes(term);
        value.hidden = !match;
        if (match) hits++;
      });

      if (empty) empty.hidden = hits > 0;
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) setOpen(false);
    });
  });
}

/* ------------------------------------------------------------------- share */

function initShare() {
  const button = root?.querySelector('[data-share]');
  if (!button) return;

  button.addEventListener('click', async () => {
    const data = { title: document.title, url: window.location.href };

    /* The native sheet where it exists; a clipboard copy everywhere else. */
    if (navigator.share) {
      try {
        await navigator.share(data);
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(data.url);
      const original = button.getAttribute('aria-label');
      button.setAttribute('aria-label', strings.shareCopied || '');
      setTimeout(() => button.setAttribute('aria-label', original), 1600);
    } catch (error) {
      console.error(error);
    }
  });
}

/* --------------------------------------------------------- recently viewed */

const RECENT_KEY = 'th:recent';
const RECENT_MAX = 8;

function trackRecent() {
  const handle = root?.dataset.productHandle;
  if (!handle) return;

  let list = [];
  try {
    list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch (error) {
    list = [];
  }

  /* Drop this product before unshifting, so a re-visit moves it to the front. */
  list = [handle, ...list.filter((h) => h !== handle)].slice(0, RECENT_MAX);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list));

  return list.filter((h) => h !== handle);
}

async function renderRecent(handles) {
  const block = document.querySelector('[data-recent]');
  const grid = document.querySelector('[data-recent-grid]');
  if (!block || !grid || !handles?.length) return;

  /*
   * Each card is fetched from its own product page and lifted out of the DOM. Slower than a
   * JSON endpoint, but the markup then matches the product cards everywhere else exactly —
   * no second card template to keep in step.
   */
  const cards = await Promise.all(
    handles.slice(0, 4).map(async (handle) => {
      try {
        const response = await fetch(`/products/${handle}`);
        if (!response.ok) return null;

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const title = parsed.querySelector('.pdp__title')?.textContent?.trim();
        const image = parsed.querySelector('.gal__img')?.getAttribute('src');
        const price = parsed.querySelector('[data-product-price] .price__amount')?.textContent?.trim();
        if (!title) return null;

        return `
          <li>
            <a href="/products/${handle}" class="card">
              <div class="card__media">
                ${image ? `<img class="card__img" src="${image}" alt="" loading="lazy">` : ''}
              </div>
              <span class="card__title">${title}</span>
              <span class="card__price"><span class="price__amount">${price || ''}</span></span>
            </a>
          </li>`;
      } catch (error) {
        return null;
      }
    })
  );

  const html = cards.filter(Boolean).join('');
  if (!html) return;

  grid.innerHTML = html;
  block.hidden = false;
}

/* -------------------------------------------------------------------- boot */

initGallery();
initZoom();
initVariants();
initBuyNow();
initOptionModal();
initShare();
initDeliveryEta();
initOfferCountdown();
initBuyBar();
renderRecent(trackRecent());
